import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { ShoppingBag, Mail, Phone, User, MapPin, Home } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const PurchasedContactsPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [purchasedContacts, setPurchasedContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    
    const fetchPurchasedContacts = async () => {
      try {
        setLoading(true);
        const data = await apiService.getPurchasedContacts();
        // Map the API response to include both property and contact info
        const mapped = (data.items || []).map((item: any) => {
          const l = item.listing || item;
          return {
            id: l.id,
            title: l.title,
            description: l.description ?? '',
            price: l.price ?? 0,
            address: {
              street: l.address ?? '',
              city: l.city ?? '',
              state: '',
              zipCode: l.zip_code ?? l.zipCode ?? '',
              country: l.country ?? ''
            },
            images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
            type: 'apartment',
            status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
            features: {
              bedrooms: l.rooms ?? 0,
              bathrooms: l.bathrooms ?? 0,
              area: Number(l.area_m2 ?? l.areaM2 ?? 0),
              garage: false,
              yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
            },
            contactInfo: {
              name: l.contact_name ?? l.contactName ?? item.contact_name ?? '',
              phone: l.contact_phone ?? l.contactPhone ?? item.contact_phone ?? '',
              email: l.contact_email ?? l.contactEmail ?? item.contact_email ?? ''
            },
            owner: l.owner_user_id ?? l.ownerUserId ?? '',
            createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
            updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
          };
        });
        setPurchasedContacts(mapped);
      } catch (e: any) {
        setError(e?.message || t('home.errors.failedToLoadListings'));
      } finally {
        setLoading(false);
      }
    };
    fetchPurchasedContacts();
  }, [user, t]);

  const handlePropertyClick = (id: string) => {
    navigate(`/property/${id}`);
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // If not authenticated and not loading, the useEffect will redirect
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <ShoppingBag size={32} className="text-primary-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">
              {t('profile.purchasedContacts')}
            </h1>
          </div>
          <p className="text-gray-600">
            {t('profile.purchasedContactsDescription')}
          </p>
        </div>
        
        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}
        
        {error && <p className="text-red-600">{error}</p>}
        
        {!loading && !error && purchasedContacts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag size={64} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4 text-lg">
              {t('profile.noPurchasedContacts') || 'You have not purchased any contacts yet'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              {t('common.browseProperties')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {purchasedContacts.map((property) => (
              <div 
                key={property.id} 
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Property Image */}
                  <div 
                    className="sm:w-48 h-48 sm:h-auto bg-gray-200 flex-shrink-0 cursor-pointer"
                    onClick={() => handlePropertyClick(property.id)}
                  >
                    {property.images && property.images.length > 0 ? (
                      <img
                        src={property.images[0]}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Home size={48} />
                      </div>
                    )}
                  </div>
                  
                  {/* Property and Contact Info */}
                  <div className="flex-1 p-6">
                    <div 
                      className="cursor-pointer mb-4"
                      onClick={() => handlePropertyClick(property.id)}
                    >
                      <h3 className="text-lg font-bold text-gray-900 mb-1 hover:text-primary-500">
                        {property.title}
                      </h3>
                      <div className="flex items-center text-gray-600 text-sm mb-2">
                        <MapPin size={14} className="mr-1" />
                        <span>{property.address.city}, {property.address.country}</span>
                      </div>
                      <p className="text-xl font-bold text-brand-dark">
                        {property.status === 'rent'
                          ? `${formatCurrency(property.price)}/${t('property.perMonth')}`
                          : formatCurrency(property.price)}
                      </p>
                    </div>
                    
                    {/* Contact Information */}
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        {t('property.contactInfo')}
                      </h4>
                      <div className="space-y-2">
                        {property.contactInfo.name && (
                          <div className="flex items-center text-sm">
                            <User size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{property.contactInfo.name}</span>
                          </div>
                        )}
                        {property.contactInfo.phone && (
                          <div className="flex items-center text-sm">
                            <Phone size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                            <a 
                              href={`tel:${property.contactInfo.phone}`}
                              className="text-primary-500 hover:underline"
                            >
                              {property.contactInfo.phone}
                            </a>
                          </div>
                        )}
                        {property.contactInfo.email && (
                          <div className="flex items-center text-sm">
                            <Mail size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                            <a 
                              href={`mailto:${property.contactInfo.email}`}
                              className="text-primary-500 hover:underline"
                            >
                              {property.contactInfo.email}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchasedContactsPage;
